import { TaskList } from '@fleet-bffs/api-contract';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InModalMessageProps } from '~/components/views/common/styled/Modal/types';
import { Task } from '~/data/Asset';
import {
  applyTaskListToSchedule,
  convertTimeWiseToWeeklySchedule,
  convertToDayWiseTasks,
  mergeTimeWiseToWeeklySchedule,
  weeklyScheduleToTimeWise,
} from '../../components/RoboticMowerSchedule/utils';
import { DayName } from './day-names';
import {
  AreaDetailsInShort,
  EditModeSchedule,
  HoverState,
  schedulesV2defaultState,
  TimeWiseTaskRecord,
  WeeklyScheduleRecord,
} from './index';

/**
 * Payload interfaces for action creators
 */
interface OpenSchedulePayload {
  assetIds: string;
  tasklist: TaskList;
  showSchedulesDifferWarning: boolean;
  missionId: number | null;
}

interface ChangeWorkAreaPayload {
  selectedMissionId: number | null;
  tasklist: TaskList;
}

interface TaskListDayUpdate {
  taskList: Task[];
  missionId?: number | string;
}

/**
 * Create the Redux slice with actions and reducers
 */
const slice = createSlice({
  name: 'robotic-mower-schedule-v2',
  initialState: schedulesV2defaultState,
  reducers: {
    setSaveEnabled(state, action: PayloadAction<boolean>) {
      state.isSaveEnabled = action.payload;
    },

    // (removed) updateMowerConfigs - previously used only by reducer tests

    updateSelectedMissionId(state, action: PayloadAction<number>) {
      state.selectedMissionId = action.payload;
    },

    updateTaskListForDay(state, action: PayloadAction<Record<DayName, TaskListDayUpdate>>) {
      // Skip updates when in edit mode to prevent resetting the edit form
      if (state.editMode) {
        return;
      }

      const updatedTasks = action.payload;

      for (const [day, { taskList, missionId }] of Object.entries(updatedTasks)) {
        const dayName = day as DayName;
        if (!state.weeklyScheduleToDisplay?.[dayName]) {
          continue;
        }

        // If missionId is provided, update only tasks for that mission
        if (missionId === undefined) {
          // Otherwise replace the entire task list
          state.weeklyScheduleToDisplay[dayName].taskList = taskList;
        } else {
          const existingTaskList = state.weeklyScheduleToDisplay[dayName].taskList ?? [];
          state.weeklyScheduleToDisplay[dayName].taskList = [
            ...existingTaskList.filter((task) => task.missionId !== missionId),
            ...taskList,
          ];
        }
      }
    },

    updateTaskListFromAddOrEditRecords(state, action: { payload: TimeWiseTaskRecord }) {
      const normalizedMissionId =
        state.selectedMissionId == null ||
        Number.isNaN(state.selectedMissionId) ||
        (!state.eposMower && state.selectedMissionId === 0)
          ? null
          : state.selectedMissionId;

      // Convert time-wise task records to day-wise task records
      const dayWiseTaskRecords = convertToDayWiseTasks(action.payload, normalizedMissionId);

      if (normalizedMissionId === null) {
        // Clear all days first
        const allDays: DayName[] = [
          'MONDAY',
          'TUESDAY',
          'WEDNESDAY',
          'THURSDAY',
          'FRIDAY',
          'SATURDAY',
          'SUNDAY',
        ];
        for (const day of allDays) {
          if (state.weeklyScheduleToDisplay?.[day]) {
            state.weeklyScheduleToDisplay[day].taskList = [];
          }
        }
        // Now apply only the new tasks
        applyTaskListToSchedule(
          state.weeklyScheduleToDisplay,
          dayWiseTaskRecords,
          normalizedMissionId
        );
      } else {
        // Update weeklyScheduleToDisplay, preserving tasks from other missions
        applyTaskListToSchedule(
          state.weeklyScheduleToDisplay,
          dayWiseTaskRecords,
          normalizedMissionId
        );
      }

      // DO NOT update snapshot during editing - it should stay frozen until save
      // The edit cards maintain their own local state
    },

    updateRefinedSchedule(state, action: PayloadAction<WeeklyScheduleRecord>) {
      state.weeklyScheduleToDisplay = action.payload;
    },

    updateAssetMetadata(
      state,
      action: PayloadAction<{
        supportsMultipleAreas?: boolean;
        eposMower?: boolean;
        assetName?: string;
        areas?: AreaDetailsInShort[];
      }>
    ) {
      const { supportsMultipleAreas, eposMower, assetName, areas } = action.payload;
      if (supportsMultipleAreas !== undefined) {
        state.supportsMultipleAreas = supportsMultipleAreas;
      }
      if (eposMower !== undefined) {
        state.eposMower = eposMower;
      }
      if (assetName !== undefined) {
        state.assetName = assetName;
      }
      if (areas !== undefined) {
        state.areas = areas;
      }
    },

    createWeeklyScheduleFromTasklist(state, action: PayloadAction<TaskList | undefined>) {
      const tasklist = action.payload;
      if (!tasklist || Object.keys(tasklist).length === 0) {
        state.weeklyScheduleToDisplay = schedulesV2defaultState.weeklyScheduleToDisplay;
        return;
      }

      state.weeklyScheduleToDisplay = Object.keys(tasklist).reduce((acc, day) => {
        acc[day as DayName] = {
          title: `bff.days.${day.toLowerCase()}`,
          titleInShort: `bff.days.short.${day.toLowerCase()}`,
          taskList: tasklist[day as DayName] || [],
        };
        return acc;
      }, {} as WeeklyScheduleRecord);
    },

    openScheduleModal(state, action: PayloadAction<Partial<OpenSchedulePayload>>) {
      const { assetIds, tasklist, showSchedulesDifferWarning, missionId } = action.payload;

      // Update modal state
      state.isOpen = true;
      state.assetId = assetIds;
      // Generate schedule display from tasklist if provided
      if (tasklist) {
        slice.caseReducers.createWeeklyScheduleFromTasklist(state, {
          payload: tasklist,
          type: 'createWeeklyScheduleFromTasklist',
        });
      }
      state.isBatchModeInfoVisible = showSchedulesDifferWarning ?? false;
      state.selectedMissionId = missionId ?? state.selectedMissionId;
      state.tasklist = tasklist;
    },

    openBatchScheduleModal(
      state,
      action: PayloadAction<{
        iprIds: string[];
      }>
    ) {
      const { iprIds } = action.payload;

      state.isOpen = true;
      state.editMode = true;
      state.selectedMissionId = undefined;
      state.isBatchModeInfoVisible = true;
      state.weeklyScheduleToDisplay = schedulesV2defaultState.weeklyScheduleToDisplay;
      state.batchMode = {
        isActive: true,
        assetIds: iprIds,
        mowerCount: iprIds.length,
      };
    },
    /**
     * Close the schedule modal and reset the display
     */
    closeScheduleModal(state) {
      state.isOpen = false;
      state.editMode = false;
      state.weeklyScheduleToDisplay = schedulesV2defaultState.weeklyScheduleToDisplay;
      state.message = schedulesV2defaultState.message;
      state.batchMode = schedulesV2defaultState.batchMode;
      // Reset UI state
      state.hoverState = null;
    },
    updateTimeWiseTaskRecord(state, action: PayloadAction<TimeWiseTaskRecord>) {
      state.timeWiseTaskRecord = action.payload;
      if (state.selectedMissionId !== null && state.selectedMissionId !== undefined) {
        state.weeklyScheduleToDisplay = mergeTimeWiseToWeeklySchedule(
          state.weeklyScheduleToDisplay ?? {},
          action.payload,
          state.selectedMissionId
        );
      } else {
        state.weeklyScheduleToDisplay = convertTimeWiseToWeeklySchedule(
          action.payload,
          state.selectedMissionId
        );
      }
    },
    addScheduleCard(state, action: PayloadAction<{ recordKey: string; card: EditModeSchedule }>) {
      const { recordKey, card } = action.payload;
      // Ensure time wise record exists
      state.timeWiseTaskRecord ??= {};

      // Assign position 0 for the new card (top). Shift existing records' positions down by 1.
      const existingKeys = Object.keys(state.timeWiseTaskRecord);
      for (const key of existingKeys) {
        const rec = state.timeWiseTaskRecord[key];
        if (rec) {
          rec.position = (rec.position ?? 0) + 1;
        }
      }

      const newCard: EditModeSchedule = {
        ...card,
        position: 0,
      };

      state.timeWiseTaskRecord[recordKey] = newCard;

      // Recompute derived weekly schedule preview
      state.weeklyScheduleToDisplay = convertTimeWiseToWeeklySchedule(
        state.timeWiseTaskRecord,
        state.selectedMissionId
      );
    },
    removeScheduleCard(state, action: PayloadAction<string>) {
      if (!state.timeWiseTaskRecord) {
        return;
      }

      const removedKey = action.payload;
      const removedRecord = state.timeWiseTaskRecord[removedKey];
      const removedPosition = removedRecord?.position;

      // Remove the record
      delete state.timeWiseTaskRecord[removedKey];

      // If removed had a position, shift up (decrement) any records with position > removedPosition
      if (removedPosition !== undefined) {
        for (const key of Object.keys(state.timeWiseTaskRecord)) {
          const rec = state.timeWiseTaskRecord[key];
          if (rec?.position !== undefined && rec.position > removedPosition) {
            rec.position = rec.position - 1;
          }
        }
      }

      // Recompute derived weekly schedule preview
      state.weeklyScheduleToDisplay = convertTimeWiseToWeeklySchedule(
        state.timeWiseTaskRecord,
        state.selectedMissionId
      );
    },
    /**
     * Change the selected work area and update the schedule display
     */
    changeWorkArea(state, action: PayloadAction<ChangeWorkAreaPayload>) {
      const { selectedMissionId, tasklist } = action.payload;

      // Update state
      state.selectedMissionId = selectedMissionId;

      // Generate schedule display from tasklist
      if (tasklist) {
        slice.caseReducers.createWeeklyScheduleFromTasklist(state, {
          payload: tasklist,
          type: 'createWeeklyScheduleFromTasklist',
        });
      }
    },

    /**
     * Set edit mode and create snapshot of weeklyScheduleToDisplay
     */
    setEditMode(state, action: PayloadAction<boolean>) {
      state.editMode = action.payload;

      if (action.payload) {
        // Entering edit mode - capture snapshot of current weeklyScheduleToDisplay
        state.timeWiseTaskRecord = weeklyScheduleToTimeWise(
          state.weeklyScheduleToDisplay,
          state.selectedMissionId
        );
      } else {
        // Exiting edit mode - clear snapshot
        state.timeWiseTaskRecord = undefined;
      }
    },

    /**
     * Cancel edit mode and revert to snapshot
     */
    cancelEditMode(state) {
      state.editMode = false;
      state.timeWiseTaskRecord = undefined;
    },

    /**
     * Set hover state for schedule UI interactions
     */
    setHoverState(state, action: PayloadAction<HoverState | null>) {
      state.hoverState = action.payload;
    },

    /**
     * Set a message to display in the modal
     */
    setMessage(state, action: PayloadAction<InModalMessageProps>) {
      state.message = action.payload;
    },

    /**
     * Remove the current message
     */
    removeMessage(state) {
      state.message = undefined;
    },

    /**
     * Close the batch mode info panel
     */
    closeBatchModeInfo(state) {
      state.isBatchModeInfoVisible = false;
    },

    /**
     * Reset the state to default values
     */
    resetState() {
      return schedulesV2defaultState;
    },
  },
});

export const {
  setSaveEnabled,
  updateTaskListForDay,
  updateSelectedMissionId,
  updateTaskListFromAddOrEditRecords,
  updateRefinedSchedule,
  updateAssetMetadata,
  setMessage,
  removeMessage,
  closeBatchModeInfo,
  resetState,
  openScheduleModal,
  closeScheduleModal,
  changeWorkArea,
  setEditMode,
  cancelEditMode,
  setHoverState,
  updateTimeWiseTaskRecord,
  addScheduleCard,
  removeScheduleCard,
  openBatchScheduleModal,
} = slice.actions;

export default slice.reducer;
